import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, UserPlus, MailCheck, AlertTriangle, Users, BellRing } from "lucide-react";
import { FilingPacket } from "@/pages/BDMPacket";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("grc_admin_token")}` });

export const BDMTeamPanel = ({ mlos = [], q = "" }) => {
  const [team, setTeam] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [revealed, setRevealed] = useState(null);
  const [packetFor, setPacketFor] = useState(null);

  const load = useCallback(async () => {
    try {
      const [t, a] = await Promise.all([
        axios.get(`${API}/admin/bdm-team`, { headers: authHeader() }),
        axios.get(`${API}/admin/bdm-applications`, { headers: authHeader() }).catch(() => ({ data: [] })),
      ]);
      setTeam(t.data || []);
      setApps((a.data || []).filter((x) => (x.application_type || "bdm") === "bdm"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rows = team.filter((b) => {
    if (!q.trim()) return true;
    const hay = `${b.name} ${b.email} ${b.company} ${b.city_state} ${(b.mlo_slugs || []).join(" ")}`.toLowerCase();
    return hay.includes(q.trim().toLowerCase());
  });

  const onTeamEmails = new Set(team.map((b) => (b.email || "").toLowerCase()));
  const promotable = apps.filter((a) => a.email && !onTeamEmails.has(a.email.toLowerCase()));

  const promote = async (appId) => {
    try {
      await axios.post(`${API}/admin/bdm-team/from-application/${appId}`, {}, { headers: authHeader() });
      toast.success("Added to the partner team");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add that applicant.");
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Remove ${b.name} from the partner team? Their login and shelf access go away.`)) return;
    try {
      await axios.delete(`${API}/admin/bdm-team/${b.id}`, { headers: authHeader() });
      toast.success("Removed");
      load();
    } catch {
      toast.error("Could not remove.");
    }
  };

  const accountAction = async (b, action) => {
    const confirmText = {
      create: `Create a login for ${b.name} and email them their private link?`,
      resend: `Re-send the invite to ${b.login_email || b.email}?\n\nThis issues a NEW password, so any old one stops working. Their private link is unaffected.`,
      reset: `Reset the password for ${b.login_email || b.email}?`,
      delete: `Delete the login for ${b.login_email || b.email}? They won't be able to sign in.`,
    }[action];
    if (!window.confirm(confirmText)) return;
    try {
      if (action === "delete") {
        await axios.delete(`${API}/admin/bdm-team/${b.id}/account`, { headers: authHeader() });
        toast.success("Login deleted");
      } else {
        const path = action === "create" ? "" : `/${action}`;
        const { data } = await axios.post(
          `${API}/admin/bdm-team/${b.id}/account${path}`,
          action === "create" ? { email: b.email } : {},
          { headers: authHeader() },
        );
        setRevealed({ ...data, name: b.name, kind: action === "reset" ? "reset" : "welcome" });
        if (data.email_sent) toast.success(`Emailed to ${data.email}`);
        else toast.error("Account ready, but the email did not send.");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That didn't work.");
    }
  };

  const copyLink = async (b) => {
    if (!b.access_url) {
      toast.error("No private link — issue a new one first.");
      return;
    }
    await navigator.clipboard.writeText(b.access_url);
    toast.success("Private link copied");
  };

  const regenerateLink = async (b) => {
    if (!window.confirm(`Issue a new private link for ${b.name}?\n\nTheir current link stops working straight away.`)) return;
    try {
      const { data } = await axios.post(`${API}/admin/bdm-team/${b.id}/access-link`, {}, { headers: authHeader() });
      await navigator.clipboard.writeText(data.access_url);
      toast.success("New link issued and copied");
      load();
    } catch {
      toast.error("Could not issue a new link.");
    }
  };

  const revokeLink = async (b) => {
    if (!window.confirm(`Revoke ${b.name}'s private link? They'll need a password to get in.`)) return;
    try {
      await axios.delete(`${API}/admin/bdm-team/${b.id}/access-link`, { headers: authHeader() });
      toast.success("Link revoked");
      load();
    } catch {
      toast.error("Could not revoke the link.");
    }
  };

  const logNudge = async (b) => {
    if (!window.confirm(`Email ${b.name} a reminder to log this month's activity?`)) return;
    try {
      await axios.post(`${API}/admin/bdm-team/${b.id}/log-nudge`, {}, { headers: authHeader() });
      toast.success("Reminder sent");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the reminder.");
    }
  };

  if (loading) {
    return (
      <div className="py-20 flex justify-center rounded-2xl bg-white border border-linen-border">
        <Loader2 className="animate-spin text-teal" size={26} />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-bdm-team-panel">
      <PartnerNudgeSettings />
      <div className="rounded-2xl bg-white border border-linen-border shadow-soft p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-teal">
            <Users size={13} /> Partner team
          </div>
          <h3 className="mt-1 font-heading font-medium text-xl text-navy">Who gets a dashboard</h3>
          <p className="mt-1 text-sm text-ink-500">
            Add a partner, mark which officer they work for, then send them their private link — one tap
            and they're in, no password. Their dashboard only shows folders you've tagged for them below.
          </p>
        </div>
        <button
          onClick={() => { setEditing(null); setShowAdd(true); }}
          data-testid="admin-bdm-add-btn"
          className="inline-flex items-center gap-2 text-sm font-semibold bg-teal text-white px-4 py-2 rounded-full hover:bg-teal-hover whitespace-nowrap"
        >
          <UserPlus size={15} /> Add partner
        </button>
      </div>

      {promotable.length > 0 && (
        <div className="rounded-2xl bg-linen/70 border border-linen-border p-4" data-testid="admin-bdm-promote">
          <p className="text-xs uppercase tracking-widest text-ink-500">Add from an application</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {promotable.slice(0, 12).map((a) => (
              <button
                key={a.id}
                onClick={() => promote(a.id)}
                data-testid={`admin-bdm-promote-${a.id}`}
                className="text-xs px-3 py-1.5 rounded-full bg-white border border-linen-border text-navy hover:border-teal"
              >
                + {a.full_name || a.email}
                {a.referring_mlo_slug ? <span className="text-ink-400"> · /{a.referring_mlo_slug}</span> : null}
              </button>
            ))}
          </div>
        </div>
      )}

      {revealed && (
        <div className="rounded-2xl border p-4 bg-lime/15 border-lime" data-testid="admin-bdm-credentials">
          {revealed.email_sent ? (
            <p className="flex items-center gap-1.5 text-navy font-semibold text-sm">
              <MailCheck className="w-4 h-4 text-teal" /> Emailed to {revealed.email} — credentials below (shown once):
            </p>
          ) : (
            <div className="mb-3 rounded-lg bg-red-50 border border-red-300 p-3">
              <p className="flex items-start gap-1.5 text-red-700 font-semibold text-[13px]">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                The {revealed.kind === "reset" ? "password reset" : "welcome"} email did NOT send.
              </p>
              <p className="mt-1.5 text-[12px] text-red-700 font-mono break-words">{revealed.email_error}</p>
              <p className="mt-2 text-[12px] text-ink-500">
                The login works — copy the credentials below and send them yourself, or fix the address and re-send.
              </p>
            </div>
          )}
          <p className="mt-2 text-sm text-navy"><span className="text-ink-500 text-xs uppercase tracking-widest">Email:</span> <span className="font-mono">{revealed.email}</span></p>
          <p className="mt-1 text-sm text-navy"><span className="text-ink-500 text-xs uppercase tracking-widest">Password:</span> <span className="font-mono">{revealed.password}</span></p>
          <p className="mt-2 text-[11px] text-ink-500">They sign in at /bdmlogin.</p>
          <button onClick={() => setRevealed(null)} className="mt-2 text-[11px] text-teal hover:underline">Dismiss</button>
        </div>
      )}

      {(showAdd || editing) && (
        <BDMForm
          bdm={editing}
          mlos={mlos}
          onClose={() => { setShowAdd(false); setEditing(null); }}
          onSaved={() => { setShowAdd(false); setEditing(null); load(); }}
        />
      )}

      {packetFor && (
        <div className="fixed inset-0 z-50 bg-navy/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
          <div className="w-full max-w-3xl bg-linen rounded-3xl border border-linen-border shadow-xl my-8" data-testid="admin-bdm-packet-modal">
            <div className="sticky top-0 bg-white border-b border-linen-border px-6 py-4 flex items-start justify-between gap-3 rounded-t-3xl">
              <div>
                <p className="text-[11px] uppercase tracking-widest text-teal">Month-end packet</p>
                <h3 className="font-heading font-medium text-xl text-navy mt-0.5">{packetFor.name}</h3>
                <p className="text-xs text-ink-500 mt-1">
                  Exactly what they see. Nothing is submitted from here — they sign their own form.
                </p>
              </div>
              <button
                onClick={() => setPacketFor(null)}
                data-testid="admin-bdm-packet-close"
                className="text-ink-400 hover:text-navy text-sm"
              >
                Close
              </button>
            </div>
            <div className="px-6 py-5">
              <FilingPacket authHeader={authHeader} adminBdmId={packetFor.id} />
            </div>
          </div>
        </div>
      )}

      <div className="rounded-2xl bg-white border border-linen-border shadow-soft overflow-hidden">
        {rows.length === 0 ? (
          <p className="p-6 text-sm text-ink-500">
            No partners on the team yet. Add one above, or promote an applicant.
          </p>
        ) : (
          <table className="w-full text-sm" data-testid="admin-bdm-team-table">
            <thead>
              <tr className="text-left text-ink-500 border-b border-linen-border bg-linen/50">
                <th className="p-3 font-medium">Name</th>
                <th className="p-3 font-medium">Email</th>
                <th className="p-3 font-medium">Works for</th>
                <th className="p-3 font-medium">Login</th>
                <th className="p-3 font-medium">NEXA</th>
                <th className="p-3 font-medium">Logged this month</th>
                <th className="p-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <tr key={b.id} className="border-b border-linen-border/60 hover:bg-linen/40" data-testid={`admin-bdm-row-${b.id}`}>
                  <td className="p-3">
                    <span className="font-medium text-navy">{b.name}</span>
                    {b.active === false && (
                      <span className="ml-2 text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-linen text-ink-500">Paused</span>
                    )}
                    {b.company ? <div className="text-[11px] text-ink-400">{b.company}</div> : null}
                  </td>
                  <td className="p-3 text-ink-500">{b.email}</td>
                  <td className="p-3 text-ink-500 text-xs">
                    {(b.mlos || []).length > 0 ? b.mlos.map((m) => m.name).join(", ") : <span className="text-ink-400">Unassigned</span>}
                  </td>
                  <td className="p-3">
                    {!b.has_account ? (
                      <span className="text-[10px] uppercase tracking-widest text-ink-400" data-testid={`admin-bdm-login-${b.id}`}>None</span>
                    ) : b.invite_status === "failed" ? (
                      <span
                        title={b.invite_error || ""}
                        data-testid={`admin-bdm-login-${b.id}`}
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-md bg-red-100 text-red-700 border border-red-300"
                      >
                        <AlertTriangle className="w-3 h-3" /> Email failed
                      </span>
                    ) : (
                      <span
                        data-testid={`admin-bdm-login-${b.id}`}
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-md bg-lime/25 text-navy"
                      >
                        Active
                      </span>
                    )}
                  </td>
                  <td className="p-3">
                    {b.nexa_registered ? (
                      <span
                        data-testid={`admin-bdm-nexa-${b.id}`}
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-md bg-teal-light text-teal"
                      >
                        Registered
                      </span>
                    ) : (
                      <span
                        data-testid={`admin-bdm-nexa-${b.id}`}
                        title="They haven't confirmed their NEXA registration — they can't be paid until they do."
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200"
                      >
                        Not yet
                      </span>
                    )}
                  </td>
                  <td className="p-3 tabular-nums text-navy">{b.activity_this_month ?? 0}</td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1.5 justify-end">
                      <button
                        onClick={() => copyLink(b)}
                        data-testid={`admin-bdm-copy-link-${b.id}`}
                        title={b.access_url || "No private link"}
                        className="text-xs px-2.5 py-1 rounded-lg bg-teal text-white hover:bg-teal-hover disabled:opacity-40"
                        disabled={!b.access_url}
                      >
                        Copy link
                      </button>
                      <button
                        onClick={() => setPacketFor(b)}
                        data-testid={`admin-bdm-packet-${b.id}`}
                        className="text-xs px-2.5 py-1 rounded-lg bg-linen hover:bg-linen-border text-navy"
                      >
                        Packet
                      </button>
                      <button
                        onClick={() => logNudge(b)}
                        data-testid={`admin-bdm-lognudge-${b.id}`}
                        className="text-xs px-2.5 py-1 rounded-lg bg-linen hover:bg-linen-border text-navy"
                      >
                        Nudge log
                      </button>
                      <button
                        onClick={() => { setShowAdd(false); setEditing(b); }}
                        data-testid={`admin-bdm-edit-${b.id}`}
                        className="text-xs px-2.5 py-1 rounded-lg bg-linen hover:bg-linen-border text-navy"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => (b.access_url ? revokeLink(b) : regenerateLink(b))}
                        data-testid={`admin-bdm-link-toggle-${b.id}`}
                        className="text-xs px-2.5 py-1 rounded-lg bg-linen hover:bg-linen-border text-navy"
                      >
                        {b.access_url ? "Revoke link" : "New link"}
                      </button>
                      {!b.has_account ? (
                        <button
                          onClick={() => accountAction(b, "create")}
                          data-testid={`admin-bdm-create-login-${b.id}`}
                          className="text-xs px-2.5 py-1 rounded-lg bg-navy text-white hover:bg-[#173a5f]"
                        >
                          Send invite
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => accountAction(b, "resend")}
                            data-testid={`admin-bdm-resend-${b.id}`}
                            className="text-xs px-2.5 py-1 rounded-lg bg-lime text-navy font-semibold hover:brightness-95"
                          >
                            Re-send invite
                          </button>
                          <button
                            onClick={() => accountAction(b, "reset")}
                            className="text-xs px-2.5 py-1 rounded-lg bg-linen hover:bg-linen-border text-navy"
                          >
                            Reset pw
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => remove(b)}
                        className="text-xs px-2.5 py-1 rounded-lg text-red-600 hover:bg-red-50"
                      >
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

function PartnerNudgeSettings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/admin/partner-nudge-settings`, { headers: authHeader() })
      .then((r) => setS(r.data))
      .catch(() => setS(null));
  }, []);

  if (!s) return null;

  const save = async (patch) => {
    setBusy(true);
    try {
      const { data } = await axios.put(
        `${API}/admin/partner-nudge-settings`,
        { ...s, ...patch },
        { headers: authHeader() },
      );
      setS(data);
      toast.success(data.enabled ? "Empty-log reminders are on" : "Empty-log reminders are off");
    } catch {
      toast.error("Could not save that.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white border border-linen-border shadow-soft p-5" data-testid="admin-partner-nudge-settings">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-teal">
            <BellRing size={13} /> Empty-log reminders
          </div>
          <h3 className="mt-1 font-heading font-medium text-xl text-navy">
            Chase the silent months automatically
          </h3>
          <p className="mt-1 text-sm text-ink-500">
            Emails any partner who has logged <span className="text-navy font-medium">nothing at all</span>{" "}
            this month, once every {s.cadence_days} days. Anyone with even one entry is left alone.
            {s.last_sent_count ? ` Last sweep: ${s.last_sent_count} sent.` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <label className="inline-flex items-center gap-2 text-xs text-ink-500">
            <input
              type="checkbox"
              checked={!!s.cc_admin}
              disabled={busy}
              onChange={(e) => save({ cc_admin: e.target.checked })}
              data-testid="admin-partner-nudge-cc"
            />
            Copy me
          </label>
          <button
            onClick={() => save({ enabled: !s.enabled })}
            disabled={busy}
            data-testid="admin-partner-nudge-toggle"
            className={`text-xs font-semibold px-4 py-2 rounded-full ${
              s.enabled ? "bg-lime text-navy" : "bg-navy text-white hover:bg-[#173a5f]"
            } disabled:opacity-60`}
          >
            {s.enabled ? "On — turn off" : "Turn on"}
          </button>
        </div>
      </div>
    </div>
  );
}

function BDMForm({ bdm, mlos, onClose, onSaved }) {  const [form, setForm] = useState({
    name: bdm?.name || "",
    email: bdm?.email || "",
    phone: bdm?.phone || "",
    company: bdm?.company || "",
    city_state: bdm?.city_state || "",
    notes: bdm?.notes || "",
    active: bdm?.active !== false,
    mlo_slugs: bdm?.mlo_slugs || [],
  });
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggleSlug = (slug) =>
    setForm((p) => ({
      ...p,
      mlo_slugs: p.mlo_slugs.includes(slug) ? p.mlo_slugs.filter((s) => s !== slug) : [...p.mlo_slugs, slug],
    }));

  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    setBusy(true);
    try {
      if (bdm) {
        await axios.patch(`${API}/admin/bdm-team/${bdm.id}`, form, { headers: authHeader() });
      } else {
        await axios.post(`${API}/admin/bdm-team`, form, { headers: authHeader() });
      }
      toast.success(bdm ? "Partner updated" : "Partner added");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white border border-linen-border shadow-soft p-5" data-testid="admin-bdm-form">
      <p className="text-xs uppercase tracking-widest text-teal">{bdm ? `Edit ${bdm.name}` : "New partner"}</p>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {[
          ["name", "Name"],
          ["email", "Email"],
          ["phone", "Phone"],
          ["company", "Company / brokerage"],
          ["city_state", "City, state"],
        ].map(([key, label]) => (
          <label key={key} className="block">
            <span className="text-xs uppercase tracking-widest text-ink-500">{label}</span>
            <input
              value={form[key]}
              onChange={(e) => set(key, e.target.value)}
              data-testid={`admin-bdm-field-${key}`}
              className="mt-1 w-full rounded-xl border border-linen-border p-2.5 text-sm text-navy bg-white focus:border-teal focus:outline-none"
            />
          </label>
        ))}
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-ink-500">Notes</span>
          <input
            value={form.notes}
            onChange={(e) => set("notes", e.target.value)}
            className="mt-1 w-full rounded-xl border border-linen-border p-2.5 text-sm text-navy bg-white focus:border-teal focus:outline-none"
          />
        </label>
      </div>

      <div className="mt-4">
        <span className="text-xs uppercase tracking-widest text-ink-500">Works for</span>
        <div className="mt-2 flex flex-wrap gap-2" data-testid="admin-bdm-mlo-picker">
          {mlos.map((m) => (
            <button
              key={m.slug}
              type="button"
              onClick={() => toggleSlug(m.slug)}
              data-testid={`admin-bdm-mlo-${m.slug}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                form.mlo_slugs.includes(m.slug)
                  ? "bg-navy text-white border-navy"
                  : "bg-white text-navy border-linen-border hover:border-teal"
              }`}
            >
              {m.name}
            </button>
          ))}
        </div>
        <p className="mt-1.5 text-[11px] text-ink-500">
          Drives which officer-specific folders they see, and whose print pieces appear on their dashboard.
        </p>
      </div>

      <label className="mt-4 flex items-center gap-2 text-sm text-navy">
        <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} />
        Active — can sign in
      </label>

      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onClose} className="text-sm px-4 py-2 rounded-full text-navy hover:bg-linen">Cancel</button>
        <button
          onClick={save}
          disabled={busy}
          data-testid="admin-bdm-save"
          className="inline-flex items-center gap-2 text-sm font-semibold px-5 py-2 rounded-full bg-navy text-white hover:bg-[#173a5f] disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {bdm ? "Save changes" : "Add partner"}
        </button>
      </div>
    </div>
  );
}

export default BDMTeamPanel;
